/**
 * Devuelve la etiqueta visible de un rol del sistema. Centraliza la
 * traduccion para evitar mostrar el codigo crudo "GESTOR_PROYECTO" en UI.
 */
export const ROL_LABELS = {
    superadmin: 'Super Admin',
    admin: 'Admin',
    gestor_proyecto: 'Gestor de Proyectos',
    comercial: 'Comercial',
    personal: 'Personal'
};
export function rolLabel(rol) {
    return ROL_LABELS[rol] || rol || '';
}

/**
 * Genera una contraseña segura garantizando AL MENOS:
 *   - 1 mayuscula  (excluyendo I y O para evitar confusion con l/0)
 *   - 1 minuscula  (excluyendo l)
 *   - 1 numero     (excluyendo 0 y 1 para no confundir con O/l)
 *   - 1 caracter especial
 * El resto se rellena con cualquier tipo y se mezcla con Fisher-Yates.
 *
 * @param {number} length longitud minima 4, default 12
 * @returns {string}
 */
export function generarPasswordSeguro(length = 12) {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghjkmnpqrstuvwxyz';
    const nums  = '23456789';
    const sym   = '!@#$%&*?';
    const all   = upper + lower + nums + sym;

    const pick = (set) => set[Math.floor(Math.random() * set.length)];
    const len  = Math.max(4, length);

    // Garantizar 1 de cada tipo
    const chars = [pick(upper), pick(lower), pick(nums), pick(sym)];

    // Rellenar el resto con cualquier caracter
    for (let i = chars.length; i < len; i++) chars.push(pick(all));

    // Fisher-Yates shuffle para que el orden no sea predecible
    for (let i = chars.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
}

// Generar ID único
export const safeId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Date.now() + Math.random());

// Formatear moneda
export function moneyFmt(amount, currency) {
    const locale = currency === "PEN" ? "es-PE" : "en-US";
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
    }).format(num);
}

// Sanitizar nombre de archivo
export function sanitizeFilename(s) {
    return (s || "documento")
        .toString()
        .trim()
        .replace(/[\\/:*?"<>|]/g, "")
        .replace(/\s+/g, "_")
        .slice(0, 80);
}

// Nomenclatura de archivos de cotizacion: COT-<numero>-<cliente>[-MANUAL]
// Sin empresa cae al nombre del cliente, que siempre existe.
// El backend replica esta convencion al renombrar los archivos subidos.
export function nombreArchivoCotizacion(cotizacion, { manual = false } = {}) {
    const numero = (cotizacion?.numero || "SN").toString().trim();
    const cliente = (cotizacion?.cliente_empresa || "").trim()
        || (cotizacion?.cliente_nombre || "").trim();

    const base = ["COT", numero, cliente].filter(Boolean).join("-");
    return sanitizeFilename(manual ? `${base}-MANUAL` : base);
}

// Convertir decimal a porcentaje para input
export function toPercentInput(decimal) {
    const n = Number(decimal);
    if (!Number.isFinite(n)) return "";
    return String(Math.round(n * 100 * 100) / 100);
}

// Convertir porcentaje a decimal
export function percentToDecimal(p) {
    const n = Number(p);
    if (!Number.isFinite(n)) return 0;
    return n / 100;
}

/**
 * Calcula el total dado un costo/subtotal y una rentabilidad (decimal: 0.68, -0.5, etc).
 *
 *   rent > 0  → margen sobre el total. total = costo / (1 - rent).
 *               rent=0.5 → total = costo × 2 (50% del total es ganancia).
 *   rent = 0  → sin ganancia. total = costo.
 *   rent < 0  → descuento sobre el costo. total = costo × (1 + rent).
 *               rent=-0.5 → total = costo × 0.5 (50% de descuento).
 *   rent ≥ 1  → no valido (margen infinito). Se devuelve el costo como safety.
 */
export function totalConRentabilidad(costo, rent) {
    const c = parseFloat(costo) || 0;
    const r = parseFloat(rent) || 0;
    if (r >= 1) return c;
    if (r < 0) return c * (1 + r);
    return c / (1 - r);
}

/**
 * Calcula la ganancia (puede ser negativa si hay descuento).
 *   ganancia = total - costo
 */
export function gananciaConRentabilidad(costo, rent) {
    return totalConRentabilidad(costo, rent) - (parseFloat(costo) || 0);
}

/**
 * `true` si el override esta definido (no es null/undefined/cadena vacia).
 * Un 0 explicito SI se considera override valido (= "regalado").
 */
export function hasOverride(override) {
    return override !== null && override !== undefined && override !== '';
}

/**
 * Total efectivo de una fila: usa override si existe, sino calcula con rentabilidad.
 *   rowTotal(1000, 0.68, null)   -> 3125  (calculado)
 *   rowTotal(1000, 0.68, 2500)   -> 2500  (override)
 */
export function rowTotal(subtotal, rent, override) {
    if (hasOverride(override)) return parseFloat(override) || 0;
    return totalConRentabilidad(subtotal, rent);
}

/**
 * Ganancia efectiva de una fila (incluyendo override).
 *   ganancia = totalEfectivo - subtotal
 */
export function rowGanancia(subtotal, rent, override) {
    return rowTotal(subtotal, rent, override) - (parseFloat(subtotal) || 0);
}

// =====================================================
// Variaciones de precio (descuentos + extras)
// =====================================================
//
// Cada variacion tiene: { tipo: 'descuento'|'extra', modalidad: 'porcentaje'|'monto',
//                         valor: number, etiqueta?: string, orden?: number }
//
// Se aplican secuencialmente sobre el resultado de la anterior, en el orden
// definido por el campo `orden` (menor primero).
//
// Estas funciones son las que consume la UI para mostrar los totales despues
// de aplicar variaciones. El backend usa las mismas equivalencias en server/utils/variaciones.js.

/**
 * Aplica una lista de variaciones sobre un valor base.
 * Devuelve el valor final (clip a 0 si algo lo dejaria negativo).
 */
export function aplicarVariaciones(base, variaciones) {
    if (!variaciones || variaciones.length === 0) return parseFloat(base) || 0;
    let actual = parseFloat(base) || 0;
    const ordenadas = [...variaciones].sort((a, b) => (a.orden || 0) - (b.orden || 0));
    for (const v of ordenadas) {
        const valor = parseFloat(v.valor) || 0;
        const signo = v.tipo === 'extra' ? 1 : -1;
        if (v.modalidad === 'porcentaje') {
            actual += (actual * valor / 100) * signo;
        } else {
            actual += valor * signo;
        }
    }
    return Math.max(0, actual);
}

/**
 * Total efectivo de una fila con variaciones (Fase 1: asignaciones + costos fijos).
 *   1. Base = override (si existe) o total calculado con rentabilidad
 *   2. Se aplican las variaciones sobre esa base
 */
export function rowTotalConVariaciones(subtotal, rent, override, variaciones) {
    const base = rowTotal(subtotal, rent, override);
    return aplicarVariaciones(base, variaciones);
}

/**
 * Ganancia efectiva incluyendo variaciones.
 *   ganancia = totalConVariaciones - subtotal
 * (Puede ser negativa si los descuentos son grandes)
 */
export function rowGananciaConVariaciones(subtotal, rent, override, variaciones) {
    return rowTotalConVariaciones(subtotal, rent, override, variaciones) - (parseFloat(subtotal) || 0);
}

/**
 * Aplica una oferta comercial sobre un precio base y devuelve el precio final.
 *
 * Oferta:
 *   { modalidad: 'porcentaje' | 'monto' | 'gratis', valor, motivo }
 *
 * Reglas:
 *   - Sin oferta → devuelve el precio original.
 *   - modalidad 'gratis' → 0 (independiente del valor).
 *   - modalidad 'porcentaje' → precio * (1 - valor/100), clip a >= 0.
 *   - modalidad 'monto' → precio - valor, clip a >= 0.
 *
 * NUNCA devuelve un negativo (aunque el frontend valida, es defensa en profundidad).
 */
/**
 * Distribuye un total entre las cuotas de un plan de pago segun el porcentaje
 * de cada una. Redondea a 2 decimales por cuota; el residuo de redondeo se
 * aplica a la cuota con MAYOR porcentaje (empate → primera). Asi las cuotas
 * con el mismo porcentaje quedan con el MISMO monto y la suma total coincide
 * exactamente con el total dado.
 * Retorna un array [{ ...periodo, monto }] en el mismo orden de entrada.
 */
export function calcularMontosPlanPago(total, periodos) {
    const t = parseFloat(total) || 0;
    const arr = Array.isArray(periodos) ? periodos : [];
    if (arr.length === 0) return [];
    const round2 = (n) => Math.round(n * 100) / 100;

    const montos = arr.map(p => round2(t * ((parseFloat(p.porcentaje) || 0) / 100)));
    const residuo = round2(t - montos.reduce((a, b) => a + b, 0));
    if (residuo !== 0) {
        // La cuota mas grande (mayor %) absorbe la diferencia — no rompe la
        // simetria visual entre cuotas iguales, que es el patron esperado.
        let idxAjuste = 0;
        let maxPct = parseFloat(arr[0].porcentaje) || 0;
        for (let i = 1; i < arr.length; i++) {
            const pct = parseFloat(arr[i].porcentaje) || 0;
            if (pct > maxPct) { maxPct = pct; idxAjuste = i; }
        }
        montos[idxAjuste] = round2(montos[idxAjuste] + residuo);
    }
    return arr.map((p, idx) => ({ ...p, monto: montos[idx] }));
}

export function aplicarOferta(precio, oferta) {
    const p = parseFloat(precio) || 0;
    if (!oferta || !oferta.modalidad) return p;
    if (oferta.modalidad === 'gratis') return 0;
    const valor = parseFloat(oferta.valor) || 0;
    if (oferta.modalidad === 'porcentaje') {
        const descuento = p * (valor / 100);
        return Math.max(0, p - descuento);
    }
    if (oferta.modalidad === 'monto') {
        return Math.max(0, p - valor);
    }
    return p;
}

/**
 * Devuelve la etiqueta legible para el badge/tooltip de una oferta.
 *   { modalidad: 'gratis' }              -> 'Gratis'
 *   { modalidad: 'porcentaje', valor: 25 } -> '-25%'
 *   { modalidad: 'monto', valor: 100 }     -> '-S/ 100.00' (segun moneda)
 */
export function etiquetaOferta(oferta, moneda = 'PEN') {
    if (!oferta || !oferta.modalidad) return '';
    if (oferta.modalidad === 'gratis') return 'Gratis';
    if (oferta.modalidad === 'porcentaje') return `-${parseFloat(oferta.valor) || 0}%`;
    if (oferta.modalidad === 'monto') return `-${moneyFmt(oferta.valor, moneda)}`;
    return '';
}

/**
 * Valida que una oferta no lleve el precio final a negativo.
 * Devuelve { ok, message?, maxValor? }.
 * - Para porcentaje: el max es 100.
 * - Para monto: el max es el precio base.
 * - Para gratis: siempre ok.
 */
export function validarOferta(oferta, precioBase, moneda = 'PEN') {
    if (!oferta || !oferta.modalidad) return { ok: true };
    if (oferta.modalidad === 'gratis') return { ok: true };
    const p = parseFloat(precioBase) || 0;
    const valor = parseFloat(oferta.valor);
    if (isNaN(valor) || valor < 0) {
        return { ok: false, message: 'El valor debe ser mayor o igual a 0' };
    }
    if (oferta.modalidad === 'porcentaje') {
        if (valor > 100) return { ok: false, message: 'El descuento no puede superar el 100%', maxValor: 100 };
        return { ok: true, maxValor: 100 };
    }
    if (oferta.modalidad === 'monto') {
        if (valor > p) {
            return { ok: false, message: `No puedes colocar un monto mayor a ${moneyFmt(p, moneda)}`, maxValor: p };
        }
        return { ok: true, maxValor: p };
    }
    return { ok: true };
}

/**
 * Valida una variacion individual.
 * @param {object} v
 * @param {number} baseActual - para validar que descuento por monto no exceda la base
 * @returns {{ok: boolean, message?: string}}
 */
export function validarVariacion(v, baseActual) {
    if (!v.tipo || !['descuento', 'extra'].includes(v.tipo)) {
        return { ok: false, message: 'Tipo inválido' };
    }
    if (!v.modalidad || !['porcentaje', 'monto'].includes(v.modalidad)) {
        return { ok: false, message: 'Modalidad inválida' };
    }
    const valor = parseFloat(v.valor);
    if (isNaN(valor) || valor < 1) {
        return { ok: false, message: 'El valor debe ser mayor o igual a 1' };
    }
    if (v.tipo === 'descuento') {
        if (v.modalidad === 'porcentaje' && valor > 100) {
            return { ok: false, message: 'Un descuento por porcentaje no puede superar 100%' };
        }
        if (v.modalidad === 'monto' && baseActual != null && valor > baseActual) {
            return {
                ok: false,
                message: `El descuento (${valor.toFixed(2)}) no puede superar la base (${baseActual.toFixed(2)})`
            };
        }
    }
    // Extras: sin limite superior (por decision del usuario)
    return { ok: true };
}

// Convertir imagen a DataURL
export async function fetchAsDataURL(url) {
    try {
        const res = await fetch(url);
        const blob = await res.blob();
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
}

// Convertir cualquier imagen (incluido SVG/WEBP) a PNG DataURL via canvas.
// Necesario para embeber el logo en el PDF (react-pdf no soporta SVG inline,
// y rasterizar evita problemas de CORS con URLs externas).
export async function imageToPngDataURL(url, targetSize = 200) {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                // Mantener proporcion, ajustar al targetSize
                const aspect = img.width / img.height;
                let w, h;
                if (aspect >= 1) {
                    w = targetSize;
                    h = targetSize / aspect;
                } else {
                    h = targetSize;
                    w = targetSize * aspect;
                }
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL('image/png'));
            } catch (err) {
                console.error('Error convirtiendo imagen a PNG:', err);
                resolve(null);
            }
        };
        img.onerror = () => {
            console.error('Error cargando imagen:', url);
            resolve(null);
        };
        img.src = url;
    });
}

// Formatear fecha
export function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
}

// Formato "15 jun 2026 14:32" para metadatos cortos (ej: ultima actualizacion).
// Devuelve "" si la fecha es invalida o vacia.
export function formatDateTimeShort(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const fecha = date.toLocaleDateString('es-PE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    }).replace(/\./g, '');
    const hora = date.toLocaleTimeString('es-PE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
    return `${fecha} ${hora}`;
}

// Tiempo relativo corto y consistente:
// "hoy" / "ayer" / "hace X d" / "hace X sem" / "hace X mes(es)" / "hace X año(s)".
// Pensado para timestamps de actividad en celdas compactas. La fecha exacta
// vive en el tooltip para quien la necesite.
export function formatRelativeShort(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return formatDate(dateString); // futuro: fecha exacta
    if (diffDays === 0) return 'hoy';
    if (diffDays === 1) return 'ayer';
    if (diffDays < 7) return `hace ${diffDays} d`;
    if (diffDays < 30) return `hace ${Math.floor(diffDays / 7)} sem`;
    // 30+ dias: fecha exacta corta "15 may 2026"
    return date.toLocaleDateString('es-PE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    }).replace(/\./g, '');
}

// Normaliza una URL base introducida por el usuario:
//   - Agrega "https://" si no tiene protocolo
//   - Quita trailing slash ("https://miweb.com/" → "https://miweb.com")
//   - Trim de espacios
// Util para no duplicar barras al concatenar paths (/cotizaciones/123).
// Si recibe vacio devuelve ''.
export function normalizarBaseUrl(raw) {
    if (!raw || typeof raw !== 'string') return '';
    let url = raw.trim();
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) {
        url = `https://${url}`;
    }
    return url.replace(/\/+$/, '');
}

// Obtener fecha actual en formato YYYY-MM-DD (fecha local, no UTC)
export function getCurrentDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

// Obtener color de estado
export function getEstadoColor(estado) {
    const colores = {
        borrador: '#6b7280',
        enviada: '#3b82f6',
        aprobada: '#10b981',
        rechazada: '#ef4444'
    };
    return colores[estado] || '#6b7280';
}

